import { Link } from 'react-router';
import { Building2, Calendar, Video } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import StatusBadge from '@/components/StatusBadge';
import { getTeamStatusDisplay, getProjectTeamStatus } from '@/utils/status';
import { formatDate } from '@/lib/utils';
import type { ProjectRole } from '@/utils/status';
import type { Project } from '../index';

/**
 * Renders a clickable project card for the team dashboard.
 *
 * Displays the project's team status, name, client, video count, and delivery date. The card links to the project's detail page.
 *
 * @param project - The project to display
 * @param projectRole - The user's role within the project team
 */
export default function TeamDashboardProjectCard({
  project,
  projectRole,
}: {
  project: Project;
  projectRole: ProjectRole;
}) {
  const teamStatuses = project.deliverables.flatMap((d) => {
    const status = d.deliverables_team?.team_status;
    const stored = status ? [status] : [];
    return d.is_ingesting ? ['processing', ...stored] : stored;
  });

  const statusCode = getProjectTeamStatus(teamStatuses, projectRole);
  const { label, colour, pulse } = getTeamStatusDisplay(
    statusCode,
    projectRole
  );
  const videoCount = project.deliverables.length;

  return (
    <Link
      to={`projects/${project.id}`}
      className="inline-flex shrink-0 cursor-pointer"
    >
      <Card className="inline-flex flex-col items-start gap-4 transition-colors hover:bg-muted/50">
        <div className="flex flex-row items-center gap-3 px-6 font-semibold leading-none">
          <StatusBadge label={label} colour={colour} pulse={pulse} />
          <Separator orientation="vertical" className="h-6! w-0.5!" />
          <span className="whitespace-nowrap">{project.project_name}</span>
        </div>
        <div className="flex flex-row flex-wrap items-center gap-x-8 gap-y-2 px-6 text-xs">
          <Tooltip delayDuration={800}>
            <TooltipTrigger className="flex w-fit flex-row items-center gap-3 whitespace-nowrap cursor-pointer">
              <Building2 className="h-4 w-4 shrink-0" />
              <span>{project.clients?.company_name ?? '-'}</span>
            </TooltipTrigger>
            <TooltipContent>
              <p>Client</p>
            </TooltipContent>
          </Tooltip>
          <Tooltip delayDuration={800}>
            <TooltipTrigger className="flex w-fit flex-row items-center gap-3 whitespace-nowrap cursor-pointer">
              <Video className="h-4 w-4 shrink-0" />
              <span>
                {videoCount} {videoCount === 1 ? 'video' : 'videos'}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>Video Count</p>
            </TooltipContent>
          </Tooltip>
          <Tooltip delayDuration={800}>
            <TooltipTrigger className="flex w-fit flex-row items-center gap-3 whitespace-nowrap cursor-pointer">
              <Calendar className="h-4 w-4 shrink-0" />
              <span>
                {project.due_date ? formatDate(project.due_date) : '-'}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>Deliver by</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </Card>
    </Link>
  );
}
